import React, { useState, useEffect } from 'react';
import { 
  Shield, Lock, Hash, Globe, BookOpen, 
  
  WifiHigh, Check, Grid
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ContactSection from './ContactUs';
import Footer from './Footer';
import Team from './Team';
import layerFFLogo from '../../assets/images/layerff-icon.png';



const heroSlides = [
  {
    title: "Welcome to 0x10LayerFF",
    subtitle: "A Cybersecurity Vanguard",
    description: "Join an elite network of digital defenders and offensive security experts pushing the boundaries of cyber resilience.",
    backgroundImage: "https://miro.medium.com/v2/resize:fit:540/1*37n30-VL2M5nIhuIoj64QQ.jpeg",
    darkOverlay: true,
    cta: "Enter the Firewall",
    primaryButton: {
      text: "Access Tools",
      link: "/tools"
    }
  },
  {
    title: "Fortify Your Digital Perimeter",
    description: "Deploy state-of-the-art security solutions to safeguard your assets from emerging threats.",
    backgroundImage: "https://img.freepik.com/premium-photo/cyber-security-business-technology-antivirus-alert-protection-security-cyber-security-firewall-cybersecurity-information-technology_36325-4689.jpg",
    darkOverlay: true,
    features: [
      "Advanced Cryptography - Unbreakable encryption protocols",
      "Secure Data Erasure - Military-grade deletion techniques",
      "Next-Gen Firewall - AI-powered intrusion prevention"
    ],
    primaryButton: {
      text: "Access Tools",
      link: "/tools"
    }
  },
  {
    title: "Cyber Intelligence & Reconnaissance",
    description: "Stay ahead of the curve with cutting-edge intelligence gathering and deep threat analysis.",
    backgroundImage: "https://workplaceprivacyreport.lexblogplatformthree.com/wp-content/uploads/sites/938/2021/01/biometric-facial.jpg",
    darkOverlay: false,
    features: [
      "Network Forensics - Identify vulnerabilities in real-time",
      "Identity Verification - Cross-platform phone & email analysis",
      "SSO Panel - Seamless and secure authentication"
    ],
    primaryButton: {
      text: "Access Tools",
      link: "/tools"
    }
  },
  {
    title: "The Cybersecurity Academy",
    description: "Empower yourself with knowledge and hands-on training from industry leaders.",
    backgroundImage: "https://datascience.columbia.edu/wp-content/uploads/2021/03/data-science-teamwork.jpg",
    darkOverlay: true,
    features: [
      "Live Webinars - Up-to-date threat briefings from experts",
      "Red Team Strategies - Offensive security methodologies",
      "Darknet Intelligence - Monitor underground cyber activities"
    ],
    primaryButton: {
      text: "Access Tools",
      link: "/tools"
    }
  },
  {
    title: "Join the 0x10LayerFF Collective",
    description: "Step into the future of cybersecurity. Become part of a mission-driven force.",
    backgroundImage: "https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    darkOverlay: true,
    cta: "Initiate Protocol",
    primaryButton: {
      text: "Access Tools",
      link: "/tools"
    }
  }
];


const toolCategories = [
  {
    name: 'Defensive Arsenal',
    tools: [
      { 
        name: 'Cryptography Suite', 
        icon: <Lock className="w-8 h-8 text-purple-400" />,
        description: 'Advanced encryption and decryption technologies',
        link: '/crypto' // Redirect link
      },            
      {
        name: 'Hashing Tools',
        icon: <Hash className="w-8 h-8 text-blue-400" />,
        description: 'Generate and verify file hashes using MD5, SHA-256, and more',
        link: '/hashing' // Redirect link
      },      
    ]
  },
  {
    name: 'Intelligence Gathering',
    tools: [        
      { 
        name: 'Network Diagnostics', 
        icon: <WifiHigh className="w-8 h-8 text-cyan-400" />,
        description: 'Comprehensive network performance analysis',
        link: '/CognitoNet' // Redirect link
      },
      {
        name: 'Phone and Email Analysis',
        icon: <Globe className="w-8 h-8 text-purple-600" />,
        description: 'Analyze email validity and formatting, and check phone number validity and details.',
        link: '/TraceMaster' 
      },
      {
        name: 'Vulnerability Scanner',
        icon: <Shield className="w-8 h-8 text-orange-400" />,
        description: 'Scan for vulnerabilities within your systems and applications.',
        link: '/VScanner' 
      },
      {
        name: 'Subnet Calculator',
        icon: <Grid className="w-8 h-8 text-teal-400" />,
        description: 'Automatically calculate subnet ranges and CIDR notations.',
        link: '/SubnetIQ' 
      },        
    ]
  },
  {
    name: 'EduConnect',  
    tools: [
      {
        name: 'Educational Resources',
        icon: <BookOpen className="w-8 h-8 text-blue-500" />,
        description: 'Access a variety of educational materials and resources',
        link: '/EduConnect' 
      },    
    ]
  },
  {
      name: 'Security & Metadata Tools',
      tools: [
        {
          name: 'MetaFlyse Metadata Tool',
          icon: <Shield className="w-8 h-8 text-green-500" />,
          description: 'Extract, edit, add, or delete metadata in files, including hidden files.',
          link: '/metaflyse'
        },
        {
          name: 'TriSec GraphAuth',
          icon: <Lock className="w-8 h-8 text-red-500" />,
          description: 'Authenticate using a three-level image password system for layered security.',
          link: '/3lelvelAuth'
        }
      ]
    }
];
const pricingTiers = [
  {
    name: 'Starter',
    price: '$0', // Set the price to $0 for the free tier
    features: [
      'Access to Educational Resources',
      'Limited Webinar Participation',
      'Basic Community Access'
    ],
    recommended: false
  },
  {
    name: 'Professional',
    price: '$39', // Set this to the lowest price
    features: [
      'Full Defensive Arsenal',
      'Advanced Network Monitoring',
      'Comprehensive Encryption',
      'HTML and PDF reports',
      'Priority Support'
    ],
    recommended: true
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    features: [
      'Complete Security Suite',
      'AI-Powered Threat Detection',
      '24/7 Dedicated Support',
      'Custom Integration'
    ],
    recommended: false
  }
];

// Custom Button Component (without generic imports)
const PremiumButton = ({ children, variant = 'primary', className = '', ...props }) => {
  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white',
    secondary: 'bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-200',
    outline: 'border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white'
  };

  return (
    <button 
      className={`
        px-6 py-3 rounded-lg transition-all duration-300 
        flex items-center justify-center space-x-2
        font-semibold text-sm uppercase tracking-wider
        ${variants[variant]}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
};



const Home = () => {
  const navigate = useNavigate(); 
  const [activeSlide, setActiveSlide] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  

  const handleGetStartedClick = () => {
    navigate('/landing'); // Redirect to the login page
  };


    // Auto-slide effect
    useEffect(() => {
      const interval = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % heroSlides.length);
      }, 10000); // Change slide every 5 seconds
  
      return () => clearInterval(interval);
    }, []);
  

  return (
    <div className="bg-gray-950 text-gray-100 min-h-screen">
      {/* Navigation */}
      <nav className={`
        fixed top-0 left-0 right-0 z-50 
        bg-gray-900/80 backdrop-blur-md
        border-b border-gray-800
        transition-all duration-300
        ${scrollPosition > 50 ? 'py-4 shadow-xl' : 'py-6'}
      `}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4">
                          <img src={layerFFLogo} alt="Logo" className="w-10  rounded-full h-15" />   
                        </div>
            <h1 className="text-2xl font-bold tracking-tight">OX10LayerFF Pro</h1>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="hover:text-blue-400 transition">Features</a>
            <a href="#pricing" className="hover:text-blue-400 transition">Pricing</a>
            <a href="#team"  className="hover:text-blue-400 transition">Team</a>
            <a href="#contact" className="hover:text-blue-400 transition">Contact</a>
            <PremiumButton onClick={handleGetStartedClick}>Get Started</PremiumButton>
          </div>
        </div>
      </nav>

      {/* Hero Section with Carousel */}
      <header id="features" className="relative pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-8">
        <div className="relative h-[450px] md:h-[500px] w-full flex items-center justify-center overflow-hidden rounded-xl shadow-2xl mx-auto my-10 max-w-7xl border border-gray-800/50">
      {/* Background Images */}
      <div className="absolute inset-0 w-full h-full rounded-xl overflow-hidden">
        {heroSlides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 w-full h-full transition-all duration-1000 ${
              activeSlide === idx ? "opacity-100" : "opacity-0"
            }`}
          >
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url("${slide.backgroundImage}")`,
                filter: "contrast(1.1) brightness(0.8)"
              }}
            />
            <div className={`absolute inset-0 w-full h-full ${
              slide.darkOverlay ? 
              "bg-gradient-to-tr from-black/80 via-black/60 to-black/50" : 
              "bg-gradient-to-b from-black/70 via-black/50 to-black/70"
            }`} />
          </div>
        ))}
      </div>

      {/* Content Container */}
      <div className="relative z-10 container mx-auto px-6 py-10 text-center text-white max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="relative overflow-hidden h-32">
            {heroSlides.map((slide, idx) => (
              <div
                key={idx}
                className={`absolute inset-x-0 top-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                  activeSlide === idx ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-5"
                }`}
              >
                <h2 className="text-4xl md:text-5xl font-extrabold leading-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
                  {slide.title}
                </h2>
                {slide.subtitle && (
                  <p className="text-lg md:text-xl text-cyan-200 mt-2 font-mono tracking-wider">
                    {slide.subtitle}
                  </p>
                )}
              </div>
            ))}
          </div>
          
          {/* Description Section */}
          <div className="relative overflow-hidden min-h-[100px]">
            {heroSlides.map((slide, idx) => (
              <div
                key={idx}
                className={`absolute inset-x-0 transition-all duration-700 delay-100 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                  activeSlide === idx ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                }`}
              >
                <p className="text-lg md:text-xl text-gray-200 mb-4">
                  {slide.description}
                </p>
                
                {/* Features List */}
                {slide.features && (
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left max-w-2xl mx-auto mt-6">
                    {slide.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start">
                        <span className="text-cyan-400 mr-2 text-lg">⟣</span>
                        <span className="text-gray-300 text-sm md:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="relative h-12 mt-6">
            {heroSlides.map((slide, idx) => (
              <div
                key={idx}
                className={`absolute inset-x-0 transition-all duration-500 ${
                  activeSlide === idx ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="flex justify-center gap-4">
                <PremiumButton onClick={handleGetStartedClick}>Get Started</PremiumButton>
                </div>
              </div>
            ))}
          </div>
          
          {/* Cyber-style Slide Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {heroSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSlide(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  activeSlide === idx ? 
                  "bg-gradient-to-r from-blue-400 to-cyan-400 w-8" : 
                  "bg-gray-500 hover:bg-gray-400 w-3"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
      </div>
          <div className="hidden md:block">
            <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700 max-h-[500px] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-4 text-center sticky top-0 bg-gray-800/50 z-10">Tool Categories</h3>
              {toolCategories.map((category, idx) => (
                <div key={idx} className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-blue-400">{category.name}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {category.tools.map((tool, toolIdx) => (
                      <div 
                        key={toolIdx} 
                        className="bg-gray-900 rounded-lg p-4 flex items-center space-x-3 hover:bg-gray-800 transition cursor-pointer" // Add cursor-pointer to indicate it's clickable
                        onClick={() => navigate(tool.link)} // Use navigate to redirect
                      >
                        {tool.icon}
                        <div>
                          <h5 className="font-medium">{tool.name}</h5>
                          <p className="text-xs text-gray-500">{tool.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      
       {/* Expert Team Section */}
      <section id="team"  className="bg-gray-950 py-16">
        <Team />
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Pricing Plans</h2>
            <p className="text-xl text-gray-400">Flexible solutions for every cybersecurity need</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, idx) => (
              <div 
                key={idx} 
                className={`
                  bg-gray-800 rounded-2xl p-8 border 
                  ${tier.recommended ? 'border-blue-500 ring-2 ring-blue-600 scale-105' : 'border-gray-700'}
                  transition-all duration-300
                `}
              >
                <h3 className="text-2xl font-bold mb-4">{tier.name}</h3>
                <div className="text-4xl font-extrabold mb-6">
                  {tier.price}
                  <span className="text-base text-gray-500">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-center space-x-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <PremiumButton className="w-full">
                  Choose {tier.name}
                </PremiumButton>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="bg-gray-950 py-16">
        <ContactSection />
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 border-t border-gray-800 py-16">
        <Footer />
      </footer>
    </div>
  );
};

export default Home;
